import React, { useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  CalendarMonth,
  Assessment,
  DirectionsCar,
  TwoWheeler,
} from "@mui/icons-material";
import api from "../../services/api";
import { getWashTypeLabel } from "../../constants/washTypes";

const WashReport = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const years = Array.from(
    { length: 2 },
    (_, i) => new Date().getFullYear() - i
  );
  const monthNames = [
    { name: "Jan", value: 1 },
    { name: "Feb", value: 2 },
    { name: "Mar", value: 3 },
    { name: "Apr", value: 4 },
    { name: "May", value: 5 },
    { name: "Jun", value: 6 },
    { name: "Jul", value: 7 },
    { name: "Aug", value: 8 },
    { name: "Sep", value: 9 },
    { name: "Oct", value: 10 },
    { name: "Nov", value: 11 },
    { name: "Dec", value: 12 },
  ];

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    setReport(null);
    try {
      const response = await api.get(`/report/wash/${year}/${month}`);
      setReport(response.data);
    } catch (err) {
      setError("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const renderWorkerEarnings = (workers) => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <b>Name</b>
            </TableCell>
            <TableCell>
              <b>Wash Type</b>
            </TableCell>
            <TableCell>
              <b>Bookings</b>
            </TableCell>
            <TableCell>
              <b>Total Earnings (₹)</b>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {workers.map((w, index) => (
            <TableRow key={index}>
              <TableCell>{w.workerName}</TableCell>
              <TableCell>
                {getWashTypeLabel(w.washType)}
              </TableCell>
              <TableCell>{w.bookingCount}</TableCell>
              <TableCell>{w.totalAmount}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        <Assessment sx={{ verticalAlign: "middle", mr: 1 }} />
        Wash Report
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl>
          <InputLabel>Year</InputLabel>
          <Select
            value={year}
            label="Year"
            onChange={(e) => setYear(e.target.value)}
            size="small"
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <InputLabel>Month</InputLabel>
          <Select
            value={month}
            label="Month"
            onChange={(e) => setMonth(e.target.value)}
            size="small"
          >
            {monthNames.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={fetchReport}
          startIcon={<CalendarMonth />}
        >
          Get Report
        </Button>
      </Box>

      {loading && (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report && (
        <Box>
          <Typography variant="h6" sx={{ mt: 2 }}>
            <DirectionsCar sx={{ verticalAlign: "middle", mr: 1 }} />
            Car Wash Report ({report.monthWiseCarWashReport.month}/
            {report.monthWiseCarWashReport.year})
          </Typography>
          <Typography>
            Total Completed Car Washes:{" "}
            <b>{report.monthWiseCarWashReport.totalCompletedCarWash}</b>
          </Typography>
          <Typography>
            Total Car Wash Amount: ₹
            <b>{report.monthWiseCarWashReport.totalCarWashAmount}</b>
          </Typography>
          <Typography>
            Total Car Wash Earning for Partners: ₹
            <b>
              {report.monthWiseCarWashReport.totalCarWashEarningsForPartners}
            </b>
          </Typography>
          {renderWorkerEarnings(
            report.monthWiseCarWashReport.workerWiseEarnings
          )}

          <Typography variant="h6" sx={{ mt: 4 }}>
            <TwoWheeler sx={{ verticalAlign: "middle", mr: 1 }} />
            Bike Wash Report ({report.monthWiseBikWashReport.month}/
            {report.monthWiseBikWashReport.year})
          </Typography>
          <Typography>
            Total Completed Bike Washes:{" "}
            <b>{report.monthWiseBikWashReport.totalCompletedBikeWash}</b>
          </Typography>
          <Typography>
            Total Bike Wash Amount: ₹
            <b>{report.monthWiseBikWashReport.totalBikeWashAmount}</b>
          </Typography>
          <Typography>
            Total Bike Wash Earning for Partners: ₹
            <b>
              {report.monthWiseBikWashReport.totalBikeWashEarningsForPartners}
            </b>
          </Typography>
          {renderWorkerEarnings(
            report.monthWiseBikWashReport.workerWiseEarnings
          )}
        </Box>
      )}
    </Box>
  );
};

export default WashReport;
